// client/src/pages/ArticleListPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PageWrapper from '../components/PageWrapper';
import ArticleSidebar from '../components/ArticleSidebar';
import '../styles/ArticleListPage.css';

// Composants SVG inline
const IconCalendar = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconFilter = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const ArticleListPage = () => {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [papabili, setPapabili] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback pour les articles sans image
  const defaultImage = '/images/article-default.png';
  
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        
        // Récupérer les articles mis en avant uniquement si on est sur la première page et sans filtres
        if (currentPage === 1 && !searchTerm && !catFilter) {
          try {
            const featuredResponse = await axios.get('/api/articles/featured');
            setFeaturedArticles(featuredResponse.data || []);
          } catch (featuredErr) {
            console.error('Erreur lors du chargement des articles mis en avant:', featuredErr);
            setFeaturedArticles([]);
          }
        }
        
        // Récupérer les articles paginés
        const params = new URLSearchParams();
        params.append('page', currentPage);
        params.append('limit', 10);
        params.append('publie', 'true');
        
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        
        if (catFilter) {
          params.append('categorie', catFilter);
        }
        
        const articlesResponse = await axios.get(`/api/articles?${params.toString()}`);
        
        if (articlesResponse.data && articlesResponse.data.articles) {
          setArticles(articlesResponse.data.articles);
          setTotalPages(articlesResponse.data.totalPages || 1);
          setTotalArticles(articlesResponse.data.total || 0);
        } else {
          // Gérer le cas où la réponse n'a pas la structure attendue
          setArticles([]);
          setTotalPages(1);
          setTotalArticles(0);
        }
        
        // Récupérer les papabili pour la sidebar
        try {
          const papabiliResponse = await axios.get('/api/cardinaux/search?papabile=true&limit=5');
          setPapabili(papabiliResponse.data.cardinaux || []);
        } catch (papabiliErr) {
          console.error('Erreur lors du chargement des papabili:', papabiliErr);
          setPapabili([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des articles:', err);
        setError('Erreur lors du chargement des données');
        setArticles([]);
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [currentPage, searchTerm, catFilter, searchSubmitted]);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, itemId) => {
    if (!originalUrl || imageErrors[itemId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (itemId) => {
    console.log(`Erreur de chargement d'image pour l'item ${itemId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [itemId]: true
    }));
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };
  
  // Extraire un résumé court (limité en caractères)
  const getShortSummary = (text, maxLength = 160) => {
    if (!text) return '';
    
    if (text.length <= maxLength) {
      return text;
    }
    
    // Couper le texte et ajouter des points de suspension
    return text.substring(0, maxLength).trim() + '...';
  };
  
  // Catégories disponibles
  const categories = [
	  { id: '', label: 'Toutes les catégories' },
	  { id: 'actualite', label: 'Actualités' },
	  { id: 'analyse', label: 'Analyses' },
	  { id: 'interview', label: 'Interviews' },
	  { id: 'evenement', label: 'Événements' },
	  { id: 'infographies', label: 'Infographies' },
	  { id: 'leon14', label: 'Leon XIV' }
	];
  
  // Gestionnaire de recherche
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Revenir à la première page lors d'une recherche
    setSearchSubmitted(!searchSubmitted); // Toggle pour forcer le rechargement
  };
  
  // Gestionnaire de changement de catégorie
  const handleCategoryChange = (e) => {
    setCatFilter(e.target.value);
    setCurrentPage(1); // Revenir à la première page lors d'un changement de filtre
  };
  
  // Pagination - Page précédente
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Pagination - Page suivante
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  // Fonction pour afficher un message adapté quand aucun article n'est trouvé
  const renderNoArticlesMessage = () => {
    if (searchTerm || catFilter) {
      return (
        <div className="no-articles">
          <p>Aucun article ne correspond à votre recherche</p>
          <button onClick={() => {
            setSearchTerm('');
            setCatFilter('');
            setCurrentPage(1);
          }} className="reset-search-button">
            Réinitialiser la recherche
          </button>
        </div>
      );
    } else {
      return (
        <div className="no-articles">
          <p>Aucun article n'est disponible pour le moment</p>
          <p className="no-articles-sub">Revenez prochainement pour découvrir nos actualités</p>
        </div>
      );
    }
  };
  
  return (
    <PageWrapper pageType="articles">
      <div className="articles-page">
        <div className="articles-header">
          <h1>Actualités du Conclave</h1>
          <p className="articles-subtitle">
            Suivez toute l'actualité du Collège des Cardinaux et des préparatifs du prochain conclave
          </p>
        </div>
        
        {/* Section articles mis en avant */}
        {!searchTerm && !catFilter && currentPage === 1 && featuredArticles.length > 0 && (
          <div className="featured-articles">
            <div className="featured-articles-grid">
              {featuredArticles.map((article, index) => (
                <Link 
                  to={`/actualites/${article.slug}`} 
                  key={article._id}
                  className={`featured-article ${index === 0 ? 'featured-main' : 'featured-secondary'}`}
                >
                  <div className="featured-image-container">
                    <img 
                      src={getApiImageUrl(article.image, article._id)} 
                      alt={article.titre} 
                      className="featured-image"
                      onError={() => handleImageError(article._id)}
                    />
                    <div className="featured-overlay"></div>
                  </div>
                  <div className="featured-content">
                    <div className="featured-category">{article.categorie}</div>
                    <h2 className="featured-title">{article.titre}</h2>
                    {index === 0 && (
                      <p className="featured-summary">{getShortSummary(article.resume, 150)}</p>
                    )}
                    <div className="featured-date">
                      <IconCalendar /> {formatDate(article.datePublication)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        
        <div className="articles-content-with-sidebar">
          <div className="articles-main-content">
            {/* Filtres et recherche */}
            <div className="articles-filters">
              <form onSubmit={handleSearch} className="search-form">
                <div className="search-input-container">
                  <IconSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Rechercher un article..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="clear-search"
                      onClick={() => {
                        setSearchTerm('');
                        setSearchSubmitted(!searchSubmitted);
                      }}
                      aria-label="Effacer la recherche"
                    >
                      ×
                    </button>
                  )}
                </div>
                <button type="submit" className="search-button">Rechercher</button>
              </form>
              
              <div className="filter-dropdown">
                <div className="filter-label">
                  <IconFilter /> Filtrer par:
                </div>
                <select
                  value={catFilter}
                  onChange={handleCategoryChange}
                  className="category-filter"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Résultats de recherche */}
            {searchTerm && (
              <div className="search-results-header">
                <h3>Résultats de recherche pour: <span>"{searchTerm}"</span></h3>
                <p>{totalArticles} article{totalArticles !== 1 ? 's' : ''} trouvé{totalArticles !== 1 ? 's' : ''}</p>
              </div>
            )}
            
            {/* Liste des articles */}
            {loading ? (
              <div className="loading-articles">
                <div className="loading-spinner"></div>
                <p>Chargement des articles...</p>
              </div>
            ) : error ? (
              <div className="error-message">
                <p>{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="reload-button"
                >
                  Réessayer
                </button>
              </div>
            ) : articles.length === 0 ? (
              renderNoArticlesMessage()
            ) : (
              <div className="articles-list">
                {articles.map(article => (
                  <article key={article._id} className="article-card">
                    <Link to={`/actualites/${article.slug}`} className="article-image-link">
                      <img 
                        src={getApiImageUrl(article.image, article._id)} 
                        alt={article.titre}
                        className="article-image"
                        onError={() => handleImageError(article._id)}
                      />
                    </Link>
                    <div className="article-content">
                      <div className="article-meta">
                        <span className="article-category">{article.categorie}</span>
                        <span className="article-date">{formatDate(article.datePublication)}</span>
                      </div>
                      <h3 className="article-title">
                        <Link to={`/actualites/${article.slug}`}>{article.titre}</Link>
                      </h3>
                      <p className="article-summary">{getShortSummary(article.resume)}</p>
                      <Link to={`/actualites/${article.slug}`} className="read-more">
                        Lire la suite
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {!loading && articles.length > 0 && totalPages > 1 && (
              <div className="articles-pagination">
                <button 
                  onClick={goToPreviousPage} 
                  disabled={currentPage === 1}
                  className="pagination-button"
                >
                  &laquo; Précédent
                </button>
                <div className="pagination-info">
                  Page {currentPage} sur {totalPages}
                </div>
                <button 
                  onClick={goToNextPage} 
                  disabled={currentPage === totalPages}
                  className="pagination-button"
                >
                  Suivant &raquo;
                </button>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <ArticleSidebar papabili={papabili} />
        </div>
      </div>
    </PageWrapper>
  );
};

export default ArticleListPage;